import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { img } from "@/lib/api";

const Lightbox = ({ images, i, setI, onClose, name }) => {
  const touch = useRef(0);
  const go = (d) => setI((i + d + images.length) % images.length);
  useEffect(() => {
    const k = (e) => (e.key === "Escape" ? onClose() : e.key === "ArrowRight" ? go(1) : e.key === "ArrowLeft" ? go(-1) : null);
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  });
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-plum/95 backdrop-blur-md flex flex-col" data-testid="gallery-lightbox"
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); }}>
      <div className="flex justify-between items-center p-4 text-pearl">
        <p className="text-[14px] font-bold">{name} · {i + 1} / {images.length}</p>
        <button onClick={onClose} aria-label="Close" data-testid="gallery-lightbox-close" className="w-11 h-11 rounded-[14px] bg-pearl/10 grid place-items-center"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-4 pb-4 min-h-0">
        <img src={img(images[i])} alt={name} className="max-h-full max-w-full object-contain rounded-[16px]" />
        {images.length > 1 && <>
          <button onClick={() => go(-1)} aria-label="Previous" data-testid="gallery-lightbox-prev" className="absolute left-4 w-12 h-12 rounded-[14px] bg-pearl text-plum grid place-items-center"><ChevronLeft /></button>
          <button onClick={() => go(1)} aria-label="Next" data-testid="gallery-lightbox-next" className="absolute right-4 w-12 h-12 rounded-[14px] bg-pearl text-plum grid place-items-center"><ChevronRight /></button>
        </>}
      </div>
    </motion.div>
  );
};

export const Gallery = ({ images, name }) => {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [full, setFull] = useState(false);
  const scroller = useRef(null);
  useEffect(() => setI(0), [images]);

  if (!images.length)
    return <div className="aspect-[4/5] rounded-[24px] bg-blush grid place-items-center text-plum/50 font-bold" data-testid="product-gallery">Photo coming soon</div>;

  const onScroll = () => {
    const el = scroller.current;
    if (el) setI(Math.round(el.scrollLeft / el.clientWidth));
  };
  const jump = (n) => {
    setI(n);
    scroller.current?.scrollTo({ left: n * scroller.current.clientWidth, behavior: "smooth" });
  };

  return (
    <div data-testid="product-gallery">
      {/* desktop */}
      <div className="hidden lg:flex gap-4">
        <div className="flex flex-col gap-3 w-20 flex-none">
          {images.map((u, n) => (
            <button key={u + n} onClick={() => setI(n)} data-testid={`gallery-thumb-${n}`} aria-label={`View image ${n + 1}`}
              className={`aspect-[4/5] rounded-[14px] overflow-hidden ring-2 transition ${n === i ? "ring-plum" : "ring-transparent opacity-70 hover:opacity-100"}`}>
              <img src={img(u)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <div
          className="relative flex-1 rounded-[24px] overflow-hidden bg-blush cursor-zoom-in"
          onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); }}
          onMouseLeave={() => setZoom(null)}
          onClick={() => setFull(true)}
          data-testid="gallery-main"
        >
          <AnimatePresence mode="wait">
            <motion.img key={images[i]} src={img(images[i])} alt={name} data-testid="gallery-main-image"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
              className="fit-view mx-auto transition-transform duration-200"
              style={zoom ? { transform: "scale(1.9)", transformOrigin: zoom } : undefined} />
          </AnimatePresence>
          <span className="absolute right-4 bottom-4 flex items-center gap-2 rounded-[12px] bg-pearl/90 px-3 py-2 text-[12px] font-bold pointer-events-none"><Expand className="w-4 h-4" /> Hover to zoom · click for fullscreen</span>
        </div>
      </div>

      {/* mobile */}
      <div className="lg:hidden -mx-[18px] sm:mx-0 relative">
        <div ref={scroller} onScroll={onScroll} className="flex items-center overflow-x-auto snap-x snap-mandatory no-scrollbar bg-blush sm:rounded-[24px]" data-testid="gallery-mobile-scroller">
          {images.map((u, n) => (
            <button key={u + n} onClick={() => { setI(n); setFull(true); }} className="snap-center flex-none w-full overflow-hidden">
              <img src={img(u)} alt={`${name} view ${n + 1}`} loading={n ? "lazy" : "eager"} className="block w-full h-auto" />
            </button>
          ))}
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex justify-center gap-2">
            {images.map((_, n) => (
              <button key={n} onClick={() => jump(n)} aria-label={`Image ${n + 1}`} data-testid={`gallery-dot-${n}`} className={`h-2 rounded-full transition-all ${n === i ? "w-7 bg-plum" : "w-2 bg-plum/30"}`} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>{full && <Lightbox images={images} i={i} setI={setI} name={name} onClose={() => setFull(false)} />}</AnimatePresence>
    </div>
  );
};
